import { useContext } from 'react';
import { AppContext } from './MultiAccessoryProvider';
import { AppContextType } from './types';

/**
 * Custom hook to consume the MultiAccessoryContext
 * @returns Context value containing store (state) and dispatch
 * @throws Error if used outside of MultiAccessoryProvider
 * @example
 * ```tsx
 * const { store, dispatch } = useMultiAccessoryContext();
 * const { recommendedAccessories, selectedAccessories } = store;
 * ```
 */
export const useMultiAccessoryContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error(
      'useMultiAccessoryContext must be used within a MultiAccessoryProvider. ' +
      'Make sure your component is wrapped with <MultiAccessoryProvider>.'
    );
  }
  return context;
};

/**
 * Custom hook that combines context and actions
 * Provides both state and action creators in one hook for convenience
 * @returns Object containing store (state), dispatch, and all action creators
 * @example
 * ```tsx
 * const { store, actions } = useMultiAccessory();
 * // Access state
 * const { recommendedAccessories, selectedAccessories } = store;
 * // Call actions
 * actions.setSelectedAccessories(data);
 * actions.getRecommendedAccessory(cartId);
 * ```
 */
export const useMultiAccessory = () => {
  const { store, dispatch } = useMultiAccessoryContext();
  
  // Lazy load the actions hook to avoid circular dependencies
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { useMultiAccessoryActions } = require('./useMultiAccessoryActions');
  const actions = useMultiAccessoryActions(dispatch);

  return {
    store,
    dispatch,
    actions,
  };
};
